<template>
  <div class="logos-section">
    <div class="container">
      <div class="logos-wrapper">
        <button
          class="nav-arrow nav-arrow--prev"
          type="button"
          aria-label="Предишни лога"
          :disabled="currentIndex === 0"
          @click="prev"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="logos-viewport">
          <div
            class="logos-track"
            :style="{
              '--total-count': logos.length,
              '--visible-count': visibleCount,
              '--current-index': currentIndex,
            }"
          >
            <div class="logo-item" v-for="(logo, index) in logos" :key="index">
              <img :src="logo.src" :alt="logo.alt" class="logo-image" />
            </div>
          </div>
        </div>

        <button
          class="nav-arrow nav-arrow--next"
          type="button"
          aria-label="Следващи лога"
          :disabled="currentIndex >= maxIndex"
          @click="next"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

// Media logos shown below the "Какво ни прави уникални?" section (do not affect hero logos)
const logos = ref([
  { src: '/static/media-logos/TheRecursive.png', alt: 'The Recursive' },
  { src: '/static/media-logos/BNR_Sofia_logo.png', alt: 'BNR Sofia' },
  { src: '/static/media-logos/btv.png', alt: 'BTV Bulgaria' },
  { src: '/static/media-logos/Bgonair-new2.webp', alt: 'BG On Air' },
  { src: '/static/kapital-logo.png', alt: 'Capital' },
  { src: '/static/media-logos/economic-bg-logo.svg', alt: 'Economic.bg' },
  { src: '/static/media-logos/Logo_of_BNT_1_(2018-).svg.png', alt: 'BNT 1' },
  { src: '/static/media-logos/File_000.png', alt:'Nova'},
  { src: '/static/media-logos/9_months_podcast.png', alt: '9 Months Podcast' },
  { src: '/static/media-logos/BNR_Hristo_Botev.png', alt: 'BNR Hristo Botev' },
  { src: '/static/media-logos/logo_mamamia.webp', alt:'Mamamia'},
  { src: '/static/media-logos/jenata-dnes_logo.png', alt: 'Jenata Dnes' },
  { src: '/static/media-logos/novite-roditeli.png', alt: 'Novite Roditeli'},
  { src: '/static/media-logos/actualno-new-logo.png', alt: 'Actualno'},
  // Добавяй нови лога тук — каруселът автоматично ще стане плъзгаем, когато broят надхвърли видимите слотове
]);

const currentIndex = ref(0);
const visibleCount = ref(7);

function updateVisibleCount() {
  const width = window.innerWidth;
  if (width <= 480) {
    visibleCount.value = 2;
  } else if (width <= 768) {
    visibleCount.value = 4;
  } else {
    visibleCount.value = 7;
  }
  // Ensure currentIndex stays within valid bounds after a resize
  if (currentIndex.value > maxIndex.value) {
    currentIndex.value = maxIndex.value;
  }
}

const maxIndex = computed(() =>
  Math.max(0, logos.value.length - visibleCount.value)
);

function next() {
  if (currentIndex.value < maxIndex.value) {
    currentIndex.value += 1;
  }
}

function prev() {
  if (currentIndex.value > 0) {
    currentIndex.value -= 1;
  }
}

onMounted(() => {
  updateVisibleCount();
  window.addEventListener('resize', updateVisibleCount);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateVisibleCount);
});
</script>

<style lang="scss" scoped>
.logos-section {
  padding: 20px 0 28px 0;
  background-color: transparent;
  margin: 0 0 30px 0;
}

.logos-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f7f8fb;
  border-radius: 16px;
  padding: 32px 24px;
  min-height: 160px;
}

.logos-viewport {
  flex: 1;
  overflow: hidden;
}

.logos-track {
  display: flex;
  width: calc(100% * (var(--total-count) / var(--visible-count)));
  transform: translateX(
    calc(-1 * var(--current-index) * (100% / var(--total-count)))
  );
  transition: transform 0.45s ease;
}

.logo-item {
  flex: 0 0 calc(100% / var(--total-count));
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;

  .logo-image {
    max-height: 46px;
    max-width: 150px;

    min-height: 46px;
    min-width: 70px;
    object-fit: contain;
  }
}

.nav-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: #fff;
  color: #333;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background: #eef0f5;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
}

@media (max-width: 768px) {
  .logos-section {
    padding: 10px 0 20px 0;
    margin: 10px 0;
  }

  .logos-wrapper {
    gap: 10px;
    padding: 22px 16px;
  }

  .logo-item .logo-image {
    max-height: 42px;
    max-width: 130px;
  }

  .nav-arrow {
    width: 34px;
    height: 34px;
  }
}
</style>
